import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireVerification?: boolean;
}

export default function ProtectedRoute({ children, requireVerification = false }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Set a maximum wait time of 2 seconds for auth to load
    const timeout = setTimeout(() => {
      setIsReady(true);
    }, 2000);

    // If loading completes before timeout, set ready immediately
    if (!loading) {
      setIsReady(true);
      clearTimeout(timeout);
    }

    return () => clearTimeout(timeout);
  }, [loading]);

  // Show loading only if not ready yet
  if (!isReady && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // QA mode bypass logic - ONLY for /list-space route
  const isQAModeEnabled = import.meta.env.VITE_QA_MODE === 'true';
  const isProduction = import.meta.env.VITE_PUBLIC_ENV === 'production';
  const isListSpaceRoute = location.pathname === '/list-space';
  
  // QA mode bypass: allow access to /list-space if QA mode is enabled and NOT in production
  const qaBypassActive = isQAModeEnabled && !isProduction && isListSpaceRoute;

  // If verification is required but user is not verified
  if (requireVerification && !profile?.is_verified) {
    // Allow bypass only for /list-space in QA mode (non-production)
    if (qaBypassActive) {
      return <>{children}</>;
    }

    // Check if profile is complete
    const isProfileComplete = profile?.full_name && profile?.phone;
    
    if (!isProfileComplete) {
      // Redirect to settings with verification required flag
      return <Navigate to="/settings?verification_required=true" replace />;
    }
    
    // Profile is complete but not verified
    return <Navigate to="/settings?verification_required=true" replace />;
  }

  return <>{children}</>;
}
